import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { FileSignature, MoreVertical, Eye, Trash2, AlertCircle, Plus } from 'lucide-react';
import { API_BASE } from '../helpers';
import DMCreateEnvelopeModal from './DMCreateEnvelopeModal';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  'draft', 'sent', 'viewed', 'signed', 'completed',
  'canceled', 'declined', 'expired', 'error',
];

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function cap(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// Small three-dot row menu (closes on outside click).
function RowMenu({ children }) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  // Close when the pointer leaves the trigger+panel area, keeping a ~10px
  // tolerance around the combined region so the gap between the button
  // and the menu (and brief edge crossings) don't dismiss it early.
  useEffect(() => {
    if (!open) return;
    const onMove = (e) => {
      const trg = triggerRef.current;
      const pnl = panelRef.current;
      if (!trg || !pnl) return;
      const t = trg.getBoundingClientRect();
      const p = pnl.getBoundingClientRect();
      const TOL = 10;
      if (
        e.clientX < Math.min(t.left, p.left) - TOL ||
        e.clientX > Math.max(t.right, p.right) + TOL ||
        e.clientY < Math.min(t.top, p.top) - TOL ||
        e.clientY > Math.max(t.bottom, p.bottom) + TOL
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousemove', onMove);
    return () => document.removeEventListener('mousemove', onMove);
  }, [open]);
  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    // Open upward when the default downward panel would be clipped by
    // the table wrapper (overflow:hidden) or the viewport — e.g. the
    // last row. Keeps the menu fully visible without a portal.
    const btn = triggerRef.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      const MENU_EST_HEIGHT = 96;
      const clip = btn.closest('.dm-table-wrap');
      const bottomBound = clip
        ? Math.min(window.innerHeight, clip.getBoundingClientRect().bottom)
        : window.innerHeight;
      setOpenUp(bottomBound - rect.bottom < MENU_EST_HEIGHT);
    }
    setOpen(true);
  };
  return (
    <div className="dm-menu" ref={ref}>
      <button
        type="button"
        className="dm-menu__trigger"
        ref={triggerRef}
        onClick={toggle}
        data-tooltip="Actions"
      >
        <MoreVertical size={18} />
      </button>
      {open && (
        <div
          className={`dm-menu__panel${openUp ? ' dm-row-menu--up' : ''}`}
          ref={panelRef}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * DMEnvelopeList — the default Envelopes view: search, filter, paginated
 * table. When `createMode` is true (route /dm/envelopes/new) the create
 * modal is rendered on top of the list.
 */
function DMEnvelopeList({ createMode = false }) {
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const [categories, setCategories] = useState([]);
  const [envelopes, setEnvelopes] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const hasActiveFilters =
    !!debouncedSearch || statusFilter !== 'all' || categoryFilter !== 'all' || !!dateFrom || !!dateTo;

  // Debounce the free-text search (300ms).
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    axios.get(`${API_BASE}/dm/options/document-categories`)
      .then(res => setCategories(res.data || []))
      .catch(() => setCategories([]));
  }, []);

  const fetchEnvelopes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (debouncedSearch) params.q = debouncedSearch;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (categoryFilter !== 'all') params.documentCategory = categoryFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = `${dateTo}T23:59:59`;
      const res = await axios.get(`${API_BASE}/dm/envelopes`, { params });
      setEnvelopes(res.data.envelopes || []);
      setTotalCount(res.data.totalCount || 0);
    } catch (err) {
      setError('Could not load envelopes. Please try again.');
      setEnvelopes([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, statusFilter, categoryFilter, dateFrom, dateTo]);

  useEffect(() => { fetchEnvelopes(); }, [fetchEnvelopes]);

  const clearFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setStatusFilter('all');
    setCategoryFilter('all');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Move this envelope to trash?')) return;
    try {
      await axios.delete(`${API_BASE}/dm/envelopes/${id}`);
      // BUG-18: deleting the LAST row of the LAST page left `page` pointing past
      // the end of the result set, so the refetch came back empty and the user
      // sat on a blank page with no indication why. Step back one page instead;
      // changing `page` re-creates fetchEnvelopes (it is in the useCallback deps)
      // and its effect refetches, so this is still exactly one refetch.
      if (envelopes.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchEnvelopes();
      }
    } catch (err) {
      setError('Could not delete the envelope. Please try again.');
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div>
      <div className="dm-page-header">
        <h1 className="dm-page-title">Envelopes</h1>
        <button className="crm-btn dm-btn--gold" onClick={() => navigate('/dm/envelopes/new')}>
          <Plus size={18} /> New Envelope
        </button>
      </div>

      <div className="dm-filters">
        <div className="dm-filter dm-filter--search">
          <label className="dm-filter__label">Search</label>
          <input
            type="text"
            className="dm-filter__control"
            placeholder="Search title, address, unit, job reference..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className="dm-filter">
          <label className="dm-filter__label">Status</label>
          <select
            className="dm-filter__control"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="all">All</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{cap(s)}</option>)}
          </select>
        </div>
        <div className="dm-filter">
          <label className="dm-filter__label">Document Category</label>
          <select
            className="dm-filter__control"
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
          >
            <option value="all">All</option>
            {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div className="dm-filter">
          <label className="dm-filter__label">Created from</label>
          <input
            type="date"
            className="dm-filter__control"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          />
        </div>
        <div className="dm-filter">
          <label className="dm-filter__label">Created to</label>
          <input
            type="date"
            className="dm-filter__control"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          />
        </div>
      </div>

      {error && (
        <div className="crm-error">
          <AlertCircle size={20} className="crm-error__icon" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="dm-center">
          <div className="dm-spinner" />
          <p className="crm-loading__text">Loading envelopes...</p>
        </div>
      ) : envelopes.length === 0 && !hasActiveFilters ? (
        <div className="crm-empty-state">
          <FileSignature size={48} className="crm-empty-state__icon" />
          <h3 style={{ margin: '0 0 0.5rem' }}>No envelopes yet</h3>
          <p className="crm-empty-state__text" style={{ marginBottom: '1.25rem' }}>
            Create your first envelope to send a document for signature.
          </p>
          <button className="crm-btn dm-btn--gold" onClick={() => navigate('/dm/envelopes/new')}>
            <Plus size={18} /> New Envelope
          </button>
        </div>
      ) : envelopes.length === 0 ? (
        <div className="crm-empty-state">
          <p className="crm-empty-state__text" style={{ marginBottom: '0.75rem' }}>
            No envelopes match your filters.
          </p>
          <button className="crm-btn crm-btn--secondary" onClick={clearFilters}>
            Clear filters
          </button>
        </div>
      ) : (
        <>
          <div className="dm-table-wrap">
            <table className="dm-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Vendor</th>
                  <th>Property</th>
                  <th>Job Ref</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {envelopes.map(env => (
                  <tr key={env.envelopeId}>
                    <td data-label="Title">
                      <Link className="dm-table__title-link" to={`/dm/envelopes/${env.envelopeId}`}>
                        {env.title}
                      </Link>
                      {env.provider === 'internal' && (
                        <span className="dm-badge dm-badge--provider-internal" style={{ marginLeft: 8 }}>Basic</span>
                      )}
                      {env.provider === 'dropbox-sign' && (
                        <span className="dm-badge dm-badge--provider-certified" style={{ marginLeft: 8 }}>Certified</span>
                      )}
                    </td>
                    <td data-label="Category">
                      {env.documentCategory
                        ? <span className="dm-badge dm-badge--category">{env.documentCategory}</span>
                        : <span className="dm-table__sub">—</span>}
                    </td>
                    <td data-label="Vendor">{env.vendor?.name || <span className="dm-table__sub">—</span>}</td>
                    <td data-label="Property">
                      {env.propertyAddress || env.unitIdentifier ? (
                        <>
                          <div>{env.propertyAddress || '—'}</div>
                          {env.unitIdentifier && (
                            <div className="dm-table__sub">
                              {/^unit\b/i.test(env.unitIdentifier) ? env.unitIdentifier : `Unit ${env.unitIdentifier}`}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="dm-table__sub">—</span>
                      )}
                    </td>
                    <td data-label="Job Ref">{env.jobReference || <span className="dm-table__sub">—</span>}</td>
                    <td data-label="Status">
                      <span className={`dm-badge dm-badge--${env.status}`}>{cap(env.status)}</span>
                    </td>
                    <td className="dm-table__sub" data-label="Created">{formatDate(env.createdDate)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <RowMenu>
                        <button
                          className="dm-menu__item"
                          onClick={() => navigate(`/dm/envelopes/${env.envelopeId}`)}
                        >
                          <Eye size={15} /> View
                        </button>
                        <button
                          className="dm-menu__item dm-menu__item--danger"
                          onClick={() => handleDelete(env.envelopeId)}
                        >
                          <Trash2 size={15} /> Delete
                        </button>
                      </RowMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalCount > PAGE_SIZE && (
            <div className="dm-pagination">
              <span>
                Page {page} of {totalPages} · {totalCount} envelope{totalCount === 1 ? '' : 's'}
              </span>
              <div className="dm-pagination__buttons">
                <button
                  className="crm-btn crm-btn--secondary"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <button
                  className="crm-btn crm-btn--secondary"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {createMode && (
        <DMCreateEnvelopeModal
          mode="create"
          onClose={() => navigate('/dm/envelopes')}
          onSuccess={(detail) => navigate(`/dm/envelopes/${detail.envelope.envelopeId}`)}
        />
      )}
    </div>
  );
}

export default DMEnvelopeList;
